import { brandPackagesApi } from '../api/modules/brandPackages';
import type { Site } from '../schemas/validation';
import { deriveComponentColors, isGlobalPalette } from './deriveComponentColors';

export type SiteThemePayload = Record<string, unknown> | string;

export interface ResolveSiteThemeOptions {
  /** Theme object from WebsiteRenderer (includes brand-derived stats, fonts, etc.) */
  serverTheme?: unknown;
  site?: Site | null;
  /** Fetch theme customColors by preset name (e.g. from GET /themes/:name) */
  fetchThemeByName?: (themeName: string) => Promise<Record<string, unknown> | null>;
}

function isThemeObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Resolve theme/brand colors for authenticated preview routes.
 *
 * Priority (matches PageEditor / PagePreview):
 * 1. Server-rendered theme (brand package already merged)
 * 2. Site brand package
 * 3. Named theme preset via API
 * 4. Site theme name string or "standard"
 */
export async function resolveSiteThemeForPreview({
  serverTheme,
  site,
  fetchThemeByName,
}: ResolveSiteThemeOptions): Promise<SiteThemePayload> {
  if (isThemeObject(serverTheme)) {
    return serverTheme;
  }

  const brandPackageId = site && (site as Site & { brandPackageId?: string }).brandPackageId;
  if (brandPackageId) {
    try {
      const brandPackage = await brandPackagesApi.get(brandPackageId);
      if (brandPackage) {
        const palette = (brandPackage.colors as { _palette?: unknown })?._palette;
        let derivedColors = brandPackage.colors as Record<string, unknown>;

        if (palette && isGlobalPalette(palette)) {
          derivedColors = {
            _palette: palette,
            ...deriveComponentColors(palette),
          } as Record<string, unknown>;
        }

        return {
          ...derivedColors,
          fontFamily: brandPackage.fonts?.bodyFont || 'Inter, system-ui, sans-serif',
          headingFont: brandPackage.fonts?.headingFont || 'Inter, system-ui, sans-serif',
          fontSize: brandPackage.fonts?.baseSize || '16px',
        };
      }
    } catch (error) {
      console.warn('Failed to load brand package for preview, falling back to theme:', error);
    }
  }

  const themeName = site?.theme;
  if (themeName && typeof themeName === 'string' && fetchThemeByName) {
    const customColors = await fetchThemeByName(themeName);
    if (customColors) {
      return customColors;
    }
  }

  if (typeof serverTheme === 'string' && serverTheme.length > 0) {
    return serverTheme;
  }

  if (themeName && typeof themeName === 'string') {
    return themeName;
  }

  return 'standard';
}
